import React, { useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { navItemsForRole, landingSlugForRole } from '../config/menuData.js';
import HelpDrawer from '../help/HelpDrawer.jsx';
import './AppLayout.scss';

const APP_NAME = 'BunkerOS';

/**
 * Shared default web layout for BunkerOS — a fixed left sidebar (brand +
 * slug-driven primary navigation) with a top bar for session/account chrome,
 * wrapping every page via the router `<Outlet />`.
 *
 * The sidebar suits a dense operations tool: nav items stack vertically with
 * one-word labels, so nothing overflows. Navigation is RBAC-filtered — each
 * business role sees only its own sections (see config/menuData.js). Account
 * controls are gated on auth state.
 */
export default function AppLayout() {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [helpOpen, setHelpOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const homePath = `/${landingSlugForRole(user?.role)}`;
  const displayName = user?.name || user?.email || 'Account';
  const roleLabel = user?.role || '';
  const navItems = navItemsForRole(user?.role);
  // Initials for the account avatar. Kept fully local — the app is offline-first,
  // so the chrome must never depend on an external image host.
  const initials =
    displayName
      .split(/[\s@._-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0].toUpperCase())
      .join('') || 'U';

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="app-shell">
      {/* Mobile backdrop when the sidebar is open */}
      <div
        className={`sidebar-backdrop${sidebarOpen ? ' show' : ''}`}
        onClick={closeSidebar}
        aria-hidden="true"
      />

      <aside className={`app-sidebar${sidebarOpen ? ' open' : ''}`}>
        <div className="sidebar-brand">
          <Link className="brand-link" to={homePath} onClick={closeSidebar}>
            <i className="fas fa-anchor" aria-hidden="true" />
            <span className="brand-name">{APP_NAME}</span>
          </Link>
          <button
            type="button"
            className="btn btn-sm sidebar-close d-lg-none"
            onClick={closeSidebar}
            aria-label="Close navigation"
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </div>

        <nav className="sidebar-nav" aria-label="Primary">
          {navItems.map((item) => (
            <NavLink
              key={item.slug}
              className="sidebar-link"
              to={`/${item.slug}`}
              onClick={closeSidebar}
            >
              <i className={`${item.icon} nav-icon`} aria-hidden="true" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {isAuthenticated && roleLabel && (
          <div className="sidebar-footer">
            <span className="sidebar-avatar" aria-hidden="true">
              {initials}
            </span>
            <div className="sidebar-identity">
              <span className="sidebar-name">{displayName}</span>
              <span className="sidebar-role">{roleLabel}</span>
            </div>
          </div>
        )}
      </aside>

      <div className="app-content">
        <header className="app-topbar">
          <button
            type="button"
            className="btn btn-sm topbar-toggle d-lg-none"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open navigation"
          >
            <i className="fa-solid fa-bars" aria-hidden="true" />
          </button>

          <Link className="topbar-brand d-lg-none" to={homePath}>
            <i className="fas fa-anchor" aria-hidden="true" /> {APP_NAME}
          </Link>

          <div className="topbar-actions ms-auto">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary rounded-circle account-help"
              onClick={() => setHelpOpen(true)}
              aria-label="Open help"
              title="Help"
            >
              <i className="fa-solid fa-circle-question" aria-hidden="true" />
            </button>
            {isAuthenticated ? (
              <div className="dropdown">
                <a
                  href="#account"
                  className="d-flex align-items-center gap-2 account-toggle dropdown-toggle"
                  id="accountMenu"
                  role="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <span className="account-avatar" aria-hidden="true">
                    {initials}
                  </span>
                  <span className="d-none d-lg-inline account-name">{displayName}</span>
                </a>
                <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="accountMenu">
                  <li>
                    <span className="dropdown-item-text small text-muted">
                      Signed in as
                      <br />
                      <strong>{displayName}</strong>
                      {roleLabel && (
                        <>
                          <br />
                          <span className="text-uppercase" style={{ fontSize: 11 }}>
                            {roleLabel}
                          </span>
                        </>
                      )}
                    </span>
                  </li>
                  <li>
                    <hr className="dropdown-divider" />
                  </li>
                  <li>
                    <button className="dropdown-item" type="button" onClick={handleLogout}>
                      <i className="fas fa-sign-out-alt me-2" aria-hidden="true" />
                      Log out
                    </button>
                  </li>
                </ul>
              </div>
            ) : (
              <Link className="btn btn-navy btn-sm fw-semibold" to="/login">
                <i className="fas fa-sign-in-alt me-2" aria-hidden="true" />
                Log in
              </Link>
            )}
          </div>
        </header>

        <main className="app-main flex-grow-1">
          <div className="container-fluid page-shell px-3 px-lg-4">
            <Outlet />
          </div>
        </main>

        <footer className="app-footer mt-auto">
          <div className="container-fluid d-flex flex-column flex-sm-row justify-content-between align-items-center gap-2">
            <span>&copy; {new Date().getFullYear()} {APP_NAME}. All rights reserved.</span>
            <span className="text-muted-soft">Bunker delivery operations platform</span>
          </div>
        </footer>
      </div>

      <HelpDrawer open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  );
}
